// DesktopAuthProtection.js
// Enterprise-grade desktop authentication protection
// Ensures mobile systems don't interfere with desktop auth flow

export class DesktopAuthProtection {
    static isInitialized = false;
    
    /**
     * Initialize desktop auth protection
     */
    static initialize() {
        if (this.isInitialized) return;
        
        // Only run on desktop
        const isMobile = window.serviceLocator?.get('MobileDetectionService')?.isMobile() || false;
        if (isMobile) {
            console.log('📱 Mobile detected - desktop auth protection not needed');
            return;
        }
        
        console.log('🖥️ Initializing desktop authentication protection...');
        
        // Protect auth overlay from mobile interference
        this.protectAuthOverlay();
        
        // Ensure auth buttons work independently
        this.protectAuthButtons();
        
        this.isInitialized = true;
        console.log('✅ Desktop authentication protection active');
        
        // Make available globally for debugging
        window.desktopAuthProtection = this;
    }
    
    /**
     * Protect auth overlay from mobile system interference
     */
    static protectAuthOverlay() {
        // CRITICAL FIX: Protect existing auth overlays first
        this.protectExistingAuthOverlays();
        
        // Monitor for auth overlay creation and protect it
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE && 
                        (node.id === 'auth-overlay' || node.classList?.contains('auth-dialog'))) {
                        
                        console.log('🔐 Protecting auth overlay from mobile interference');
                        
                        // Add desktop-specific protection
                        node.dataset.desktopProtected = 'true';
                        
                        // Ensure proper z-index above mobile systems
                        node.style.zIndex = '60000';
                        
                        // Disable backdrop dismissal for desktop
                        this.disableBackdropDismissal(node);
                    }
                });
            });
        });
        
        // Observe document body for auth overlay creation
        observer.observe(document.body, { childList: true, subtree: true });
        
        console.log('🔐 Auth overlay protection observer active');
    }
    
    /**
     * Protect existing auth overlays (critical for timing issues)
     */
    static protectExistingAuthOverlays() {
        console.log('🔐 Protecting existing auth overlays...');
        
        // Find all existing auth overlays
        const existingOverlays = document.querySelectorAll('#auth-overlay, .auth-dialog, [id*="auth"]');
        
        existingOverlays.forEach(overlay => {
            if (overlay.id === 'auth-overlay' || overlay.classList.contains('auth-dialog')) {
                console.log('🔐 Protecting existing auth overlay:', overlay.id);
                
                // Apply protection
                overlay.dataset.desktopProtected = 'true';
                overlay.style.zIndex = '60000';
                this.disableBackdropDismissal(overlay);
                
                console.log('✅ Existing auth overlay protected');
            }
        });
    }
    
    /**
     * Disable backdrop dismissal for desktop auth overlays
     */
    static disableBackdropDismissal(authElement) {
        // Remove any backdrop click handlers that might dismiss the overlay
        authElement.addEventListener('click', (e) => {
            // Only allow dismissal via explicit close buttons, not backdrop clicks
            if (e.target === authElement) {
                e.stopPropagation();
                e.preventDefault();
                console.log('🔐 Prevented backdrop dismissal on desktop');
            }
        }, { capture: true });
        
        console.log('🔐 Backdrop dismissal disabled for desktop auth overlay');
    }
    
    /**
     * Protect auth buttons from mobile system interference
     */
    static protectAuthButtons() {
        // Monitor for auth button creation and protect them
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Find auth-related buttons
                        const authButtons = node.querySelectorAll?.('[data-action="sign-in"], [data-action="sign-up"], #signin-submit-btn-auth, #signup-submit-btn-auth') || [];
                        
                        authButtons.forEach(button => {
                            console.log('🔐 Protecting auth button from mobile interference:', button.id || button.dataset.action);
                            
                            // Mark as desktop-protected
                            button.dataset.desktopProtected = 'true';
                            
                            // Ensure higher z-index than mobile systems
                            button.style.zIndex = '60001';
                            
                            // Add desktop-specific event handling protection
                            button.addEventListener('click', (e) => {
                                console.log('🔐 Desktop auth button clicked:', button.id || button.dataset.action);
                                // Let the click proceed to normal auth handlers
                            }, { capture: false, passive: false });
                        });
                    }
                });
            });
        });
        
        // Observe document for auth button creation
        observer.observe(document.body, { childList: true, subtree: true });
        
        console.log('🔐 Auth button protection observer active');
    }
    
    /**
     * Get protection status for debugging
     */
    static getStatus() {
        return {
            isInitialized: this.isInitialized,
            isMobile: window.serviceLocator?.get('MobileDetectionService')?.isMobile() || false,
            authOverlayExists: !!document.getElementById('auth-overlay'),
            protectedElements: document.querySelectorAll('[data-desktop-protected="true"]').length
        };
    }
}

// Make available globally for debugging
window.debugDesktopAuth = () => DesktopAuthProtection.getStatus();
